import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import Fab from '@mui/material/Fab';
import { InfoDialog } from '../ControlsClient/InfoDialog/InfoDialog';
import ChatIcon from '@mui/icons-material/Chat';
import { Popover } from '@mui/material';
import { LocalDataTrack, RemoteDataTrackPublication } from 'twilio-video';
import ChatMessageList from '../ChatMessages/ChatMessageList';
import { IMessage } from '../ChatMessages/BaseChatMessage';
import './ChatButton.scss';
import { styled } from '@mui/material/styles';
import { PSYLARIS_DARK, PSYLARIS_LIGHT_GREY } from '../../utils/Colors';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles({
  customWidth: {
    '& .MuiTooltip-tooltip': {
      width: 'max-content',
      maxWidth: '285px',
      background: '#2196f6',
      'font-size': `1rem`,
    },
  },
});

const fabStyle: React.CSSProperties = {
  margin: 10, // theme.spacing(1),
  zIndex: 10,
};

const disabledFabStyle: React.CSSProperties = {
  pointerEvents: 'none',
  margin: 10,
};

const messageHistoryStyle = {
  height: '250px',
};

interface IShowContent {
  showDialog: boolean;
  toggleDialog: () => void;
}

function ShowContent(props: IShowContent) {
  return props.showDialog ? (
    <InfoDialog
      open={props.showDialog}
      onClose={() => {
        props.toggleDialog();
      }}
    />
  ) : (
    <ChatIcon />
  );
}

const RightUpperCorner = styled('div')({
  position: 'absolute',
  left: '70%',
  top: '15%',
  borderRadius: '50%',
  border: '0.2px solid ' + PSYLARIS_DARK,
  color: PSYLARIS_DARK,
  //width: '20px', -causes the circle not to be round
  //height: '20px', -causes the circle not to be round
  size: 'xs',
  background: PSYLARIS_LIGHT_GREY, //'#ffffff',
  display: 'flex',
  justifyItems: 'center',
  alignItems: 'center',
  zIndex: 10000,
});

interface ChatProps {
  t: any;
  anchorEl: HTMLButtonElement | null;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;
  localDatatrack: LocalDataTrack;
  remoteDatatrack: RemoteDataTrackPublication | null | undefined;
  isDisconnected: boolean;
}

const ChatButton: React.FC<ChatProps> = ({
  remoteDatatrack,
  localDatatrack,
  anchorEl,
  t,
  isDisconnected,
  setAnchorEl,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([] as IMessage[]);
  const [showNotification, setShowNotification] = useState(false);
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const [receivedMessage, setReceivedMessage] = useState('');
  const TOOLTIP_TITLE = t('Chat');
  const open = Boolean(anchorEl);
  const classes = useStyles();
  const toggleDialog = () => {
    setShowDialog(!showDialog);
  };

  const notificationMessage = useMemo(() => {
    if (showNotification) {
      const length = messages.length;
      return messages[length - 1];
    }
  }, [showNotification, messages]);

  const updateInputValue = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(evt.target.value);
  };

  const sendMessage = () => {
    if (!!inputValue) {
      localDatatrack.send(JSON.stringify({ type: 'Chat', data: inputValue }));
      const iMessage: IMessage = {
        content: inputValue,
        isOwner: true,
        createdAt: new Date(),
      };
      setMessages([...messages, iMessage]);
      setInputValue('');
    }
  };

  useEffect(() => {
    if (receivedMessage) {
      const iMessage: IMessage = {
        content: receivedMessage as string,
        isOwner: false,
        createdAt: new Date(),
      };
      setMessages([...messages, iMessage]);
      if (!open) {
        setShowNotification(true);
        setShouldShowTooltip(true);
      }
      setTimeout(() => {
        setShouldShowTooltip(false);
      }, 15000);
      // Cleanup
      setReceivedMessage('');
    }
  }, [messages, receivedMessage, open]);

  useEffect(() => {
    if (remoteDatatrack) {
      remoteDatatrack.on('subscribed', (track) => {
        track.on('message', (msg: any) => {
          if (msg) {
            const { type, data } = JSON.parse(msg);
            if ((type as string) === 'Chat') {
              setReceivedMessage(data as string);
            }
          }
        });
      });
    }
    return () => {
      if (remoteDatatrack) {
        remoteDatatrack.off('subscribed', (track) => {
          track.off('message', () => {});
        });
      }
    };
  }, [remoteDatatrack]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setShowNotification(false);
    setShouldShowTooltip(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      sendMessage();
    }
  };

  return (
    <>
      <Tooltip title={TOOLTIP_TITLE} placement="top" PopperProps={{ disablePortal: true }}>
        <>
          <Fab
            style={isDisconnected ? disabledFabStyle : fabStyle}
            onClick={handleClick}
            data-testid="pwid-chat-toggle"
          >
            <ShowContent showDialog={showDialog} toggleDialog={toggleDialog} />
          </Fab>
          {showNotification ? (
            <Tooltip
              classes={{ popper: classes.customWidth }}
              title={notificationMessage?.content || ''}
              placement="top"
              PopperProps={{ disablePortal: true }}
              open={shouldShowTooltip}
            >
              <RightUpperCorner data-testid="pwid-chat-notification">
                <NotificationsActiveIcon />
              </RightUpperCorner>
            </Tooltip>
          ) : null}
        </>
      </Tooltip>

      <Popover
        id="popover-basic"
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <div
          className={'chat-message-container'}
          style={messageHistoryStyle}
          key={'chat-message-container'}
          data-testid="pwid-chat-messages"
        >
          <ChatMessageList messages={messages} />
        </div>
        <div className={'messenger-input-container'}>
          <input
            id="message_input"
            placeholder={t('Type message here')}
            value={inputValue}
            onChange={(evt) => updateInputValue(evt)}
            onKeyDown={handleKeyDown}
            autoFocus
            data-testid="pwid-chat-input"
          />
          <button onClick={sendMessage} data-testid="pwid-chat-send">
            {t('send-message')}
          </button>
        </div>
      </Popover>
    </>
  );
};
export default ChatButton;
